'use client';

import { c as _c } from "react/compiler-runtime";
import { OrganizationSwitcher } from '@clerk/nextjs';
import { Menu } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { AccountMenu } from '@/components/shell/account-menu';
import { CommandPalette, type CommandPaletteItem } from '@/components/shell/command-palette';
import { CommandPaletteTrigger } from '@/components/shell/command-palette-trigger';
import { MobileDrawer } from '@/components/shell/mobile-drawer';
import { SectionCrumb, type SectionCrumbSection } from '@/components/shell/section-crumb';
import { ShopSwitcher, type ShopSwitcherShop } from '@/components/shell/shop-switcher';
import { useBreakpoint } from '@/components/shell/use-breakpoint';
import { clerkAppearance } from '@/lib/clerk-appearance';
import logo from '@/static/logo.svg';
import { cn } from '@/utils/tailwind';
export type ShellHeaderProps = {
  shop: ShopSwitcherShop;
  shopsForSwitcher: ShopSwitcherShop[];
  commandPaletteItems: CommandPaletteItem[];
  /** Top-level nav sections used to derive the contextual crumb beside the shop switcher. */
  navSections: SectionCrumbSection[];
  /** Pre-rendered mobile nav content (server component children of MobileDrawer). Required for `<md` UX. */
  mobileNavContent: ReactNode;
};

/**
 * Top application bar for the admin shell containing the logo, the Clerk organization switcher, the
 * shop switcher, the search trigger, and the account menu.
 *
 * The {@link OrganizationSwitcher} selects the active Clerk ORG (the tenant team that owns shops);
 * `hidePersonal` keeps a personal account out of the list since every shop is org-owned. Selecting an
 * org returns to the chooser (`/`) — the storefront WITHIN an org is still chosen via the `/[domain]/`
 * route — and creating one routes to `/onboarding`. The {@link AccountMenu} (Clerk `<UserButton>`)
 * self-sources the operator from the Clerk session, so the header no longer threads a `user` prop.
 *
 * On compact breakpoints the desktop sidebar is replaced by a MobileDrawer triggered from this header.
 * The breakpoint gate is client-side to avoid a Radix useId hydration mismatch on the trigger button.
 *
 * @param props.shop - The currently active shop for the shop switcher.
 * @param props.shopsForSwitcher - All shops available for switching.
 * @param props.commandPaletteItems - Items rendered in the CommandPalette opened by the search trigger.
 * @param props.navSections - Top-level nav sections for the contextual crumb beside the shop switcher.
 * @param props.mobileNavContent - Pre-rendered server component tree rendered inside the MobileDrawer.
 */
export function ShellHeader(t0) {
  const $ = _c(21);
  const {
    shop,
    shopsForSwitcher,
    commandPaletteItems,
    navSections,
    mobileNavContent
  } = t0;
  const breakpoint = useBreakpoint();
  const isCompact = breakpoint === "mobile" || breakpoint === "tablet";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("flex h-14 w-full items-center justify-between gap-3 border-0 border-border border-b-2 bg-background px-3");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isCompact || $[2] !== mobileNavContent) {
    t2 = isCompact ? <MobileDrawer side="left" title="Navigate" trigger={<button type="button" aria-label="Open menu" className="flex h-9 w-9 items-center justify-center rounded-md border-2 border-border"><Menu className="h-5 w-5" /></button>}>{mobileNavContent}</MobileDrawer> : null;
    $[1] = isCompact;
    $[2] = mobileNavContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Link href="/" title="Nordcom Commerce" className="flex shrink-0 items-center"><Image className="h-7 w-auto object-contain object-left" src={logo} alt="Nordcom Commerce Logo" height={75} width={150} priority={true} /></Link>;
    t4 = <OrganizationSwitcher appearance={clerkAppearance} hidePersonal={true} afterSelectOrganizationUrl="/" afterCreateOrganizationUrl="/onboarding/" />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== shop || $[7] !== shopsForSwitcher) {
    t5 = <ShopSwitcher current={shop} shops={shopsForSwitcher} />;
    $[6] = shop;
    $[7] = shopsForSwitcher;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== navSections) {
    t6 = <SectionCrumb sections={navSections} />;
    $[9] = navSections;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex items-center gap-3">{t2}{t3}{t4}{t5}{t6}</div>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center gap-2"><CommandPaletteTrigger /><AccountMenu /></div>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== commandPaletteItems) {
    t9 = <CommandPalette items={commandPaletteItems} />;
    $[16] = commandPaletteItems;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <header className={t1}>{t7}{t8}{t9}</header>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}